/**
 * PerformanceChart - Reusable line chart for portfolio performance
 * Shows performance over time with optional benchmark comparison
 */

import React from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Legend, Area, AreaChart 
} from 'recharts';

export interface PerformanceDataPoint {
  date: string;
  value: number;
  benchmark?: number;
  [key: string]: any; // Allow additional series
}

export interface AdditionalSeries {
  dataKey: string;
  name: string;
  color: string;
  strokeWidth?: number;
  strokeDasharray?: string;
}

export interface PerformanceChartProps {
  data: PerformanceDataPoint[];
  title?: string;
  showBenchmark?: boolean;
  height?: number;
  chartType?: 'line' | 'area';
  valueLabel?: string;
  benchmarkLabel?: string;
  className?: string;
  colors?: {
    primary?: string;
    benchmark?: string;
  };
  additionalSeries?: AdditionalSeries[]; // Support for multiple portfolio lines
  showLegend?: boolean;
  valueFormat?: 'percentage' | 'currency' | 'number'; // How to format values
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  title,
  showBenchmark = false,
  height = 300,
  chartType = 'line',
  valueLabel = 'Portfolio',
  benchmarkLabel = 'Benchmark',
  className = '',
  colors = {
    primary: '#002B45',
    benchmark: '#F7B500'
  },
  additionalSeries = [],
  showLegend = false,
  valueFormat = 'percentage'
}) => {
  // Ensure data is an array
  const safeData = Array.isArray(data) ? data : [];

  // Return early if no data
  if (safeData.length === 0) {
    return (
      <div className={className}>
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        )}
        <div className="h-[300px] flex items-center justify-center text-gray-500">
          No data available
        </div>
      </div>
    );
  }
  // Format value based on type
  const formatValue = (value: number): string => {
    if (valueFormat === 'currency') {
      return `CHF ${(value / 1000000).toFixed(2)}M`;
    } else if (valueFormat === 'number') {
      return value.toLocaleString();
    } else {
      return `${value.toFixed(2)}%`;
    }
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded border border-gray-200">
          <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatValue(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Format Y-axis
  const formatYAxis = (value: number) => {
    if (valueFormat === 'currency') {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (valueFormat === 'number') {
      return value.toLocaleString();
    } else {
      return `${value}%`;
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className={`flex items-center justify-center h-${height} ${className}`}>
        <p className="text-gray-500">No performance data available</p>
      </div>
    );
  }

  const ChartComponent = chartType === 'area' ? AreaChart : LineChart;
  const DataComponent = (chartType === 'area' ? Area : Line) as React.ComponentType<any>;

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent data={safeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="date" 
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            tickFormatter={formatYAxis}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <Tooltip content={<CustomTooltip />} />
          {(showBenchmark || showLegend || additionalSeries.length > 0) && <Legend />}

          {/* Main consolidated line (thicker, primary color) */}
          <DataComponent
            type="monotone"
            dataKey="value"
            stroke={colors.primary}
            fill={colors.primary}
            fillOpacity={chartType === 'area' ? 0.1 : 0}
            strokeWidth={3}
            name={valueLabel}
            dot={false}
            activeDot={{ r: 6 }}
          />

          {/* Benchmark line (dashed, secondary color) */}
          {showBenchmark && (
            <DataComponent
              type="monotone"
              dataKey="benchmark"
              stroke={colors.benchmark}
              fill={colors.benchmark}
              fillOpacity={chartType === 'area' ? 0.1 : 0}
              strokeWidth={2}
              name={benchmarkLabel}
              dot={false}
              strokeDasharray="5 5"
              connectNulls={true}
            />
          )}

          {/* Additional series (e.g., individual portfolios at client level) */}
          {additionalSeries.map((series, _index) => (
            <Line
              key={series.dataKey}
              type="monotone"
              dataKey={series.dataKey}
              stroke={series.color}
              strokeWidth={series.strokeWidth || 1.5}
              strokeDasharray={series.strokeDasharray}
              name={series.name}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

export default PerformanceChart;